import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, ButtonBase, CircularProgress, FormControlLabel, Skeleton, Switch, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { AppDialog, EmptyState, KeyValueList, StatusChip } from '../ui';
import MockupEventDialog from '../MockupEventDialog';
import { IStore } from '../../store/types';
import { CustomerHistory } from '../../store/slices';
import { CustomerHistoryArgs, CustomerHistoryEntry, CustomerHistoryFilter, HISTORY_PAGE_SIZE, historyKey } from '../../store/thunk/customerHistory';
import { formatDate } from '../../lib/formatDate';
import { MIN_DATE_SENTINEL } from '../../lib/orderFilters';
import { MockupEvent, asFlag, isTestTemplate, mockupEventId, mockupStatus, percentOf, safeHref } from '../../lib/mockupEvents';
import { useStyles } from './styles';

/** The row of `/mockup/customers`, when the dialog is opened from the Customers page. */
export interface CustomerSummary {
    customerId: string | number;
    createdAt?: string | null;
    eventCount?: number;
    addedToCartCount?: number;
    orderedCount?: number;
    ipCount?: number;
    isBlocked?: number | boolean | null;
    lastEventDate?: string | null;
}

export interface CustomerHistoryDialogProps {
    open: boolean;
    customerId: string | number | null;
    onClose: () => void;
    summary?: CustomerSummary | null;
    /** The preview page's date range, offered as an optional "only this range" switch. */
    range?: { minDate?: string | null; maxDate?: string | null } | null;
}

const FILTERS: { value: CustomerHistoryFilter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'cart', label: 'In cart' },
    { value: 'ordered', label: 'Ordered' },
];

const count = (value: number | null | undefined): string | null => (value === null || value === undefined ? null : Number(value).toLocaleString('en-US'));

const Thumb = ({ url, label, classes, onClick }: { url: string | null; label: string; classes: ReturnType<typeof useStyles>; onClick: () => void }) =>
    url ? (
        <ButtonBase className={classes.thumb} onClick={onClick} aria-label={`${label}: open upload details`}>
            <img src={url} alt="" loading="lazy" className={classes.thumbImage} />
        </ButtonBase>
    ) : (
        <div className={classes.thumb} aria-hidden="true">{label}</div>
    );

const HistoryRow = ({ event, classes, onDetails }: { event: MockupEvent; classes: ReturnType<typeof useStyles>; onDetails: (event: MockupEvent) => void }) => {
    const id = mockupEventId(event);
    const status = mockupStatus(event.status);
    const inCart = asFlag(event.toCart);
    const ordered = asFlag(event.orderExist);
    const tagCount = event.tags?.length ?? 0;
    const open = () => onDetails(event);
    return (
        <li className={classes.row}>
            <div className={classes.thumbs}>
                <Thumb url={safeHref(event.imageUrl)} label="Photo" classes={classes} onClick={open} />
                <Thumb url={safeHref(event.mockupUrl)} label="Mockup" classes={classes} onClick={open} />
            </div>
            <div className={classes.main}>
                <div className={classes.line}>
                    <span className={classes.date}>{event.createdAt ? formatDate(event.createdAt, { time: true }) : '—'}</span>
                    <StatusChip tone={status.tone} size="sm">{status.label}</StatusChip>
                    {inCart && (
                        <StatusChip tone="warning" size="sm">
                            In cart{event.toCartDate ? ` · ${formatDate(event.toCartDate, { time: true })}` : ''}
                        </StatusChip>
                    )}
                    {ordered && <StatusChip tone="success" size="sm">Ordered</StatusChip>}
                    {isTestTemplate(event) && <StatusChip tone="danger" size="sm">A/B test</StatusChip>}
                </div>
                <div className={classes.product}>
                    {event.product || 'No product'}
                    {event.productSku ? <span className={classes.sku}> · {event.productSku}</span> : null}
                </div>
                <div className={classes.meta}>
                    <span>Event {id ?? '—'}</span>
                    <span>Session {event.sessionId ?? '—'}</span>
                    <span>IP {event.ip || '—'}</span>
                    <span>
                        {tagCount} {tagCount === 1 ? 'tag' : 'tags'}
                        {asFlag(event.imageRecognized) ? ' · recognized' : ''}
                    </span>
                    {event.urlPath && <span className={classes.path} title={event.urlPath}>{event.urlPath}</span>}
                </div>
            </div>
            <div className={classes.rowActions}>
                <Button size="small" variant="text" onClick={open}>Details</Button>
            </div>
        </li>
    );
};

/**
 * Every upload of one customer, newest first, 200 per page with "Load more" (the API caps a page at
 * 500 and defaults to 100). Cart / order filters and the optional date range are applied by the server.
 */
const CustomerHistoryDialog = ({ open, customerId: currentCustomerId, onClose, summary, range }: CustomerHistoryDialogProps) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    // Keep the last customer while the dialog fades out, so the content does not blank.
    const lastCustomerId = useRef<string | number | null>(null);
    if (currentCustomerId != null) lastCustomerId.current = currentCustomerId;
    const customerId = currentCustomerId ?? lastCustomerId.current;
    const [filter, setFilter] = useState<CustomerHistoryFilter>('all');
    const [rangeOnly, setRangeOnly] = useState(false);
    const [details, setDetails] = useState<MockupEvent | null>(null);

    const rangeMin = range?.minDate && range.minDate !== MIN_DATE_SENTINEL ? range.minDate : null;
    const rangeMax = range?.maxDate || null;
    const hasRange = !!(rangeMin || rangeMax);
    const args: CustomerHistoryArgs | null =
        customerId != null
            ? {
                  customerId,
                  filter,
                  minDate: rangeOnly && rangeMin ? rangeMin : undefined,
                  maxDate: rangeOnly && rangeMax ? rangeMax : undefined,
              }
            : null;
    const key = args ? historyKey(args) : '';
    const keyFor = (filter: CustomerHistoryFilter) => (customerId != null ? historyKey({ customerId, filter }) : '');
    const entry = useSelector((state: IStore) => (key ? state.customerHistory.data[key] : undefined));
    const allEntry = useSelector((state: IStore) => state.customerHistory.data[keyFor('all')]);
    const cartEntry = useSelector((state: IStore) => state.customerHistory.data[keyFor('cart')]);
    const orderedEntry = useSelector((state: IStore) => state.customerHistory.data[keyFor('ordered')]);
    /** Keys fetched during this opening: every tab / range loads once, switching back is served from the store. */
    const loadedKeys = useRef<Set<string>>(new Set());

    useEffect(() => {
        setFilter('all');
        setRangeOnly(false);
        setDetails(null);
        loadedKeys.current = new Set();
    }, [currentCustomerId, open]);

    useEffect(() => {
        if (!open || !args || loadedKeys.current.has(key)) return;
        loadedKeys.current.add(key);
        dispatch<any>(CustomerHistory.getCustomerHistory({ ...args, page: 1 }));
        // eslint-disable-next-line react-hooks/exhaustive-deps -- one first-page request per tab / range per opening (key)
    }, [open, key]);

    const items = entry?.items ?? [];
    const total = entry?.totalCount ?? 0;
    const loading = entry?.status === 'loading';
    const failed = entry?.status === 'rejected';
    const canLoadMore = items.length < total && !loading;
    const reload = () => args && dispatch<any>(CustomerHistory.getCustomerHistory({ ...args, page: 1 }));
    const loadMore = () => args && dispatch<any>(CustomerHistory.getCustomerHistory({ ...args, page: (entry?.page || 1) + 1 }));

    // Live figures come from the tabs' totals once they have loaded; until then the Customers row (if any).
    const liveTotal = (tab: CustomerHistoryEntry | undefined) => (tab?.status === 'resolved' ? tab.totalCount : null);
    const loaded = allEntry?.status === 'resolved';
    const uploads = liveTotal(allEntry) ?? summary?.eventCount ?? null;
    const cart = liveTotal(cartEntry) ?? summary?.addedToCartCount ?? null;
    const ordered = liveTotal(orderedEntry) ?? summary?.orderedCount ?? null;
    const lastUpload = (loaded ? allEntry!.items[0]?.createdAt : null) ?? summary?.lastEventDate ?? null;
    const facts = [
        { label: 'Uploads', value: count(uploads) },
        { label: 'In cart', value: cart != null ? `${count(cart)}${uploads ? ` · ${percentOf(cart, uploads)}` : ''}` : null },
        { label: 'Ordered', value: ordered != null ? `${count(ordered)}${cart ? ` · ${percentOf(ordered, cart)} of cart` : ''}` : null },
        { label: 'Customer since', value: summary?.createdAt ? formatDate(summary.createdAt) : null },
        { label: 'Last upload', value: lastUpload ? formatDate(lastUpload, { time: true }) : null },
        { label: 'IP addresses', value: count(summary?.ipCount) },
    ];

    let body: React.ReactNode;
    if (failed && items.length === 0) {
        body = <EmptyState title="The history could not be loaded" hint={entry?.error || undefined} action={<Button variant="outlined" onClick={reload}>Retry</Button>} />;
    } else if (loading && items.length === 0) {
        body = (
            <div className={classes.list} aria-busy="true">
                {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={110} />)}
            </div>
        );
    } else if (items.length === 0) {
        body = <EmptyState title="No uploads" hint={filter === 'all' && !rangeOnly ? 'This customer has no uploads yet.' : 'No uploads match this filter.'} />;
    } else {
        body = (
            <ul className={classes.list}>
                {items.map((event, index) => (
                    <HistoryRow key={`${mockupEventId(event) ?? index}`} event={event} classes={classes} onDetails={setDetails} />
                ))}
            </ul>
        );
    }

    return (
        <>
            <AppDialog
                open={open && currentCustomerId != null}
                onClose={onClose}
                maxWidth="lg"
                title={(
                    <span className={classes.title}>
                        Customer {customerId}
                        {summary && asFlag(summary.isBlocked) && <StatusChip tone="danger" size="sm">Blocked</StatusChip>}
                    </span>
                )}
                actions={<Button variant="contained" onClick={onClose}>Close</Button>}
            >
                <KeyValueList items={facts} minColumnWidth={130} className={classes.facts} />
                <div className={classes.toolbar}>
                    <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_, value) => value && setFilter(value)} aria-label="Which uploads to show">
                        {FILTERS.map((option) => (
                            <ToggleButton key={option.value} value={option.value}>{option.label}</ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                    {hasRange && (
                        <FormControlLabel
                            sx={{ m: 0 }}
                            control={<Switch size="small" checked={rangeOnly} onChange={(event) => setRangeOnly(event.target.checked)} />}
                            label={`Only ${rangeMin || '…'} – ${rangeMax || '…'}`}
                        />
                    )}
                    <span className={classes.count} aria-live="polite">
                        {loading && items.length === 0 ? 'Loading…' : `${items.length.toLocaleString('en-US')} of ${total.toLocaleString('en-US')} uploads`}
                    </span>
                </div>
                {body}
                {(canLoadMore || (loading && items.length > 0)) && (
                    <div className={classes.more}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={loadMore}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : undefined}
                        >
                            {loading ? 'Loading…' : `Load ${Math.min(HISTORY_PAGE_SIZE, total - items.length).toLocaleString('en-US')} more`}
                        </Button>
                    </div>
                )}
            </AppDialog>
            <MockupEventDialog event={details} open={!!details} onClose={() => setDetails(null)} />
        </>
    );
};

export default CustomerHistoryDialog;
